<template>
  <div class="mit-home-page mit-page">
    <v-container fluid class="pa-6">
             
      <!-- HERO / WELCOME SECTION -->
      <v-row class="mb-6">
        <v-col cols="12">
          <v-card class="pa-6 rounded-lg elevation-2 background-card" outlined>
            <v-row align="center">
              <v-col cols="12" md="8">
                <div class="text-h4 font-weight-bold mb-2">
                  Welcome to CSAIL Maps
                </div>
                <div class="text-subtitle-1 grey--text text--darken-2 mb-4">
                  Explore CSAIL facilities, search for rooms, lab groups, or equipment, and get accessible indoor turn-by-turn directions.
                </div>
                <div class="d-flex flex-wrap" style="gap: 12px;">
                  <v-btn color="primary" large elevation="1" to="/directions?b=32">
                    <v-icon left>mdi-directions</v-icon>
                    Get Directions
                  </v-btn>
                  <v-btn color="grey lighten-3" class="black--text" large elevation="0" to="/maps/32">
                    <v-icon left color="primary">mdi-map-outline</v-icon>
                    Explore Building 32
                  </v-btn>
                  <v-btn color="grey lighten-3" class="black--text" large elevation="0" to="/maps/45">
                    <v-icon left color="primary">mdi-map-outline</v-icon>
                    Explore Building 45
                  </v-btn>
                </div>
              </v-col>
              <v-col cols="12" md="4" class="text-center d-none d-md-block">
                <v-icon size="140" color="primary lighten-4">mdi-map-marker-path</v-icon>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>

      <!-- GLOBAL CAMPUS SEARCH -->
      <v-row class="mb-6">
        <v-col cols="12">
          <v-card class="pa-6 rounded-lg elevation-2" outlined>
            <v-card-title class="font-weight-bold text-h5 mb-4 pa-0">Find Something</v-card-title>
            <v-row>
              <v-col cols="12">
                <v-autocomplete
                  ref="campusSearch"
                  v-model="globalSearchSelected"
                  :search-input.sync="globalSearchText"
                  :items="filteredGlobalSpaces"
                  :loading="loadingGlobalSpaces"
                  item-text="name"
                  item-value="mitId" 
                  return-object
                  :filter="globalSearchFilter"
                  label="Search all buildings (rooms, lab groups, printers, people...)"
                  outlined
                  clearable
                  prepend-inner-icon="mdi-magnify"
                  @change="onGlobalSearchSelected"
                  hide-details
                >
                    <!-- COMPACT MOBILE-FRIENDLY FILTERS INSIDE THE MENU -->
                    <template v-slot:prepend-item>
                    <v-list-item dense class="flex-column align-start px-2 py-1 sticky-filter-bar" @click.stop>
                        
                        <!-- STATE 1: Choose Building -->
                        <div class="w-100 d-flex align-center flex-nowrap native-scroll" v-if="quickFilterBuilding === 'All' || !quickFilterBuilding">
                            <div class="text-caption font-weight-bold grey--text text--darken-1 mr-2 ml-1 flex-shrink-0">Building:</div>
                            
                            <!-- Standalone Chips (No Group) -->
                            <v-chip 
                            v-for="b in buildings" 
                            :key="b" 
                            @click="quickFilterBuilding = b"
                            :color="quickFilterBuilding === b ? 'primary' : ''"
                            :text-color="quickFilterBuilding === b ? 'white' : ''"
                            :outlined="quickFilterBuilding !== b"
                            small 
                            class="ma-1 flex-shrink-0"
                            >
                            {{b}}
                            </v-chip>
                        </div>

                        <!-- STATE 2: Building Chosen -> Show Floors -->
                        <div class="w-100 d-flex align-center flex-nowrap native-scroll" v-else>
                            <!-- Building Clear Button -->
                            <v-chip small color="primary" text-color="white" @click.stop="resetFilters" class="ma-1 flex-shrink-0" style="cursor: pointer;">
                                <v-icon left small>mdi-close-circle</v-icon> Bldg {{ quickFilterBuilding }}
                            </v-chip>
                            
                            <div class="text-caption font-weight-bold grey--text text--darken-1 ml-2 mr-1 flex-shrink-0">Floor:</div>
                            
                            <!-- Standalone Floor Chips -->
                            <v-chip 
                            @click="quickFilterFloor = 'All'"
                            :color="quickFilterFloor === 'All' ? 'primary' : ''"
                            :text-color="quickFilterFloor === 'All' ? 'white' : ''"
                            :outlined="quickFilterFloor !== 'All'"
                            small 
                            class="ma-1 flex-shrink-0"
                            >
                            All
                            </v-chip>

                            <v-chip 
                            v-for="f in availableFloors" 
                            :key="f" 
                            @click="quickFilterFloor = f"
                            :color="quickFilterFloor === f ? 'primary' : ''"
                            :text-color="quickFilterFloor === f ? 'white' : ''"
                            :outlined="quickFilterFloor !== f"
                            small 
                            class="ma-1 flex-shrink-0"
                            >
                            {{f}}
                            </v-chip>
                        </div>
                    </v-list-item>
                    </template>

                  <!-- SEARCH RESULT RENDERING -->
                  <template v-slot:item="{ item }">
                    <v-list-item-avatar>
                      <v-icon>{{ getSpaceIcon(item) }}</v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-bold">
                        {{ item.name || item.mitIdShort || item.mitId }}
                        <span v-if="item.isNeighborhood && !(item.name || '').toLowerCase().includes('group')"> Group</span>
                      </v-list-item-title>
                      <v-list-item-subtitle class="primary--text caption">
                        Building: {{ item.mitBuildingId || item.buildingMitId || (item.mitId ? item.mitId.split('-')[0] : 'Unknown') }} 
                        <span v-if="item.level">| Floor: {{ item.level }}</span> 
                        | {{ item.useType }}
                        <span v-if="item.piList && item.piList.length"> | PI: {{ Array.isArray(item.piList) ? item.piList.join(', ') : item.piList }}</span>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
              </v-col>
            </v-row>
            <div class="d-flex mt-4 flex-wrap" style="gap: 12px;">
                <span class="grey--text text-subtitle-2 align-self-center">Quick Links:</span>
                
                <v-btn small rounded color="primary" outlined @click="applyQuickLink('restroom')">
                    <v-icon left small>mdi-human-male-female</v-icon> Restrooms
                </v-btn>
                <v-btn small rounded color="primary" outlined @click="applyQuickLink('phonebooth')">
                    <v-icon left small>mdi-phone</v-icon> Phone Booths
                </v-btn>
                <v-btn small rounded color="primary" outlined @click="applyQuickLink('group')">
                    <v-icon left small>mdi-account-group</v-icon> Groups
                </v-btn>
                
                <!-- Add the new Conference Rooms button here -->
                <v-btn small rounded color="primary" outlined @click="applyQuickLink('conference')">
                    <v-icon left small>mdi-presentation</v-icon> Conference Rooms
                </v-btn>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- QUICK ACTIONS & FEATURE HIGHLIGHTS -->
      <v-row class="mb-6">
                 
        <!-- CARD 1: Indoor Directions -->
        <v-col cols="12" md="4">
          <v-card class="fill-height pa-4 rounded-lg d-flex flex-column" outlined elevation="1">
            <v-card-title class="font-weight-bold text-h6">
              <v-avatar color="blue lighten-5" size="40" class="mr-3">
                <v-icon color="primary">mdi-navigation-variant-outline</v-icon>
              </v-avatar>
              Indoor Wayfinding
            </v-card-title>
            <v-card-text class="grey--text text--darken-2 flex-grow-1">
              Find the fastest path between offices, conference rooms, labs, or amenities across floors. Supports accessibility options to avoid stairs or elevators.
            </v-card-text>
            <v-card-actions class="pt-0">
              <v-btn text color="primary" class="font-weight-bold" to="/directions?b=32">
                Launch Directions <v-icon right small>mdi-arrow-right</v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>

        <!-- CARD 2: Interactive Maps -->
        <v-col cols="12" md="4">
          <v-card class="fill-height pa-4 rounded-lg d-flex flex-column" outlined elevation="1">
            <v-card-title class="font-weight-bold text-h6">
              <v-avatar color="green lighten-5" size="40" class="mr-3">
                <v-icon color="green darken-2">mdi-office-building-marker-outline</v-icon>
              </v-avatar>
              Interactive Maps
            </v-card-title>
            <v-card-text class="grey--text text--darken-2 flex-grow-1">
              Browse detailed floor plans, locate research group neighborhoods, locate printers, phone booths, or check room capacities and seating layouts.
            </v-card-text>
            <v-card-actions class="pt-0">
              <v-btn text color="green darken-2" class="font-weight-bold" to="/maps/32">
                View Maps <v-icon right small>mdi-arrow-right</v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>

        <!-- CARD 3: QR Location Wayfinding -->
        <v-col cols="12" md="4">
          <v-card class="fill-height pa-4 rounded-lg d-flex flex-column" outlined elevation="1">
            <v-card-title class="font-weight-bold text-h6">
              <v-avatar color="purple lighten-5" size="40" class="mr-3">
                <v-icon color="purple darken-1">mdi-qrcode-scan</v-icon>
              </v-avatar>
              Instant QR Routing
            </v-card-title>
            <v-card-text class="grey--text text--darken-2 flex-grow-1">
              Scan any location QR code posted throughout CSAIL facilities to automatically lock in your current starting location and route directly to your target destination.
            </v-card-text>
            <v-card-actions class="pt-0">
              <v-btn text color="purple darken-1" class="font-weight-bold" disabled>
                Scan Posters In Hallways
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import BaseMitLoading from '../../base/components/BaseMitLoading.vue';
import MitSpacesService from '../services/MitSpacesService.js';

export default {
  name: 'MitHomePage',
  components: {
    BaseMitLoading
  },
  data: function() {
    return {
      loadingBuildings: true,
      buildings: [],
      loadingGlobalSpaces: false,
      globalSpaces: [],
      groups: [],
      globalSearchSelected: null,
      globalSearchText: null,
      quickFilterBuilding: 'All',
      quickFilterFloor: 'All'
    };
  },
  mounted() {
    this.fetchBuildings();
    this.fetchGlobalSpaces();
    this.fetchGroups();
  },
  watch: {
    // Watch for changes to the building filter
    quickFilterBuilding(val) {
      if (!val) {
        // Prevent undefined wipes
        this.quickFilterBuilding = 'All';
      } else if (val !== 'All') {
        // Automatically default floor to 'All' when a new building is selected
        this.quickFilterFloor = 'All';
      }
    },
    quickFilterFloor(val) {
      if (!val) {
        // Prevent undefined wipes
        this.quickFilterFloor = 'All';
      }
    }
  },
  computed: {
    // Combine standard spaces with group data
    combinedGlobalItems() {
      let groupItems = [];
      if (this.groups && this.groups.length > 0) {
        // 1. Filter out any groups that do not have a headSpaceId
        groupItems = this.groups.filter(g => g.headSpaceId).map(g => {
          let headSpace = this.globalSpaces.find(s => s.mitId === g.headSpaceId);
          // 2. Split on '-' and use the first part as the building id
          let bldgId = String(g.headSpaceId).split('-')[0];
          
          return {
            isNeighborhood: true,
            id: g.id,
            name: g.name,
            mitId: g.headSpaceId,
            piList: g.piList,
            useType: 'Group',
            level: headSpace ? headSpace.level : null,
            buildingMitId: bldgId // Assign the parsed building ID
          };
        });
      }
      return [...groupItems, ...this.globalSpaces];
    },
    
    // Dynamically calculate the floors that exist for the selected building
    availableFloors() {
      if (this.quickFilterBuilding === 'All') return [];
      
      const floors = new Set();
      this.combinedGlobalItems.forEach(s => {
        let bldg = s.buildingMitId || s.mitBuildingId || (s.mitId ? String(s.mitId).split('-')[0] : '');
        if (String(bldg) === String(this.quickFilterBuilding) && s.level !== undefined && s.level !== null) {
          floors.add(String(s.level));
        }
      });
      
      return Array.from(floors).sort((a, b) => a.localeCompare(b, undefined, {numeric: true}));
    },
    
    // Apply filters to the combined search array
    // Apply filters to the combined search array
    filteredGlobalSpaces() {
      let filtered = this.combinedGlobalItems;
      
      // Safe check: Only filter if a building is actively selected
      if (this.quickFilterBuilding && this.quickFilterBuilding !== 'All') {
        filtered = filtered.filter(s => {
          let bldg = s.buildingMitId || s.mitBuildingId;
          if (!bldg && s.mitId) {
             bldg = String(s.mitId).split('-')[0];
          }
          return String(bldg) === String(this.quickFilterBuilding);
        });
      }
      
      // Safe check: Only filter if a specific floor is actively selected
      if (this.quickFilterFloor && this.quickFilterFloor !== 'All') {
        filtered = filtered.filter(s => String(s.level) === String(this.quickFilterFloor));
      }
      
      return filtered;
    }
  },
  methods: {
    fetchBuildings() {
      this.loadingBuildings = true;
      MitSpacesService.getBuildingsMitIdUniqueList()
        .then((data) => {
          if(data && data.length > 0) {
              this.buildings = data.map(b => b.building_mit_id || b.mitId || b);
          }
        })
        .catch((err) => {
          console.error("Home Page: Failed to load building list", err);
        })
        .finally(() => {
          this.loadingBuildings = false;
        });
    },
    fetchGlobalSpaces() {
      this.loadingGlobalSpaces = true;
      
      MitSpacesService.getAllSpacesAndObjects()
        .then((data) => {
          // The backend returns the array inside the 'spaces' property
          this.globalSpaces = data.spaces || [];
        })
        .catch((err) => {
          console.error("Home Page: Failed to load global spaces and objects", err);
        })
        .finally(() => {
          this.loadingGlobalSpaces = false;
        });
    },
    fetchGroups() {
      MitSpacesService.getGroups()
        .then(data => {
          this.groups = data || [];
        })
        .catch(err => {
          console.error("Home Page: Failed to load groups", err);
        });
    },
    resetFilters() {
      this.quickFilterBuilding = 'All';
      this.quickFilterFloor = 'All';
    },
    onGlobalSearchSelected(item) {
      // 1. Handle clearing the search
      if (!item) {
        this.globalSearchSelected = null;
        return;
      }

      // 2. Extract building and force it to be an integer
      let bldg = item.mitBuildingId || item.buildingMitId || (item.mitId ? String(item.mitId).split('-')[0] : '32');
      let bldgInt = parseInt(bldg, 10);

      // 3. Assemble query parameters (also enforcing integer for floor level)
      let routeQuery = {};
      if (item.mitId) routeQuery.s = item.mitId;
      if (item.level) routeQuery.l = parseInt(item.level, 10);

      // 4. Route to the map
      this.$router.push({ path: `/maps/${bldgInt}`, query: routeQuery }).catch(() => {});
      
      // 5. Instantly clear the search bar so it can be reused easily
      this.$nextTick(() => {
        this.globalSearchSelected = null;
        this.globalSearchText = null;
      });
    },
    applyQuickLink(searchText) {
      // 1. Set the search text programmatically
      this.globalSearchText = searchText;
      
      // 2. Wait for Vue to update the DOM, then force the autocomplete to focus AND open
      this.$nextTick(() => {
        if (this.$refs.campusSearch) {
          this.$refs.campusSearch.focus();
          this.$refs.campusSearch.isMenuActive = true;
        }
      });
    },
    getSpaceIcon(item) {
      if (item.isNeighborhood) return 'mdi-account-group';
      if (item.spaceType === 'object') return 'mdi-cube-outline';
      if (item.useType && item.useType.toUpperCase().includes('RESTROOM')) return 'mdi-human-male-female';
      return 'mdi-office-building-outline';
    },
    globalSearchFilter(item, queryText) {
      if (!item) return false;
      const searchText = (queryText || '').toLowerCase().trim();
      if (!searchText) return true;
      
      // Check for plural aliases (so typing 'restrooms' matches 'RESTROOM')
      const singularSearch = searchText.endsWith('s') ? searchText.slice(0, -1) : searchText;

      const textMitIdShort = (item.mitIdShort || '').toLowerCase();
      const textUse = (item.useType || '').toLowerCase();
      const textSpaceType = (item.spaceType || '').toLowerCase();
      const textName = (item.name || '').toLowerCase();
      const textMitId = (item.mitId || '').toLowerCase();
      
      let piMatch = false;
      if (item.piList && Array.isArray(item.piList)) {
          piMatch = item.piList.some(pi => pi.toLowerCase().includes(searchText));
      }
      
      // Specifically ensure "group" searches catch the neighborhood items
      if (item.isNeighborhood && (searchText === 'group' || searchText === 'groups' || textName.includes(searchText) || piMatch)) {
          return true;
      }
      
      return textMitIdShort.includes(searchText) ||
             textUse.includes(searchText) || textUse.includes(singularSearch) ||
             textSpaceType.includes(searchText) || textSpaceType.includes(singularSearch) ||
             textName.includes(searchText) || textName.includes(singularSearch) ||
             textMitId.includes(searchText) ||
             piMatch;
    }
  }
};
</script>

<style lang="scss" scoped>
.mit-home-page {
  background-color: #fafafa;
  min-height: calc(100vh - 64px);

  .background-card {
    background: linear-gradient(135deg, #ffffff 0%, #f4f6f9 100%);
    border: 1px solid #e0e0e0;
  }

  /* Helper class for full width in the dropdown filters */
  .w-100 {
    width: 100%;
  }

  .hover-card {
    transition: all 0.2s ease-in-out;
    cursor: pointer;

    &:hover {
      border-color: #1976d2;
      background-color: #f0f7ff;
      transform: translateY(-2px);
    }
  }

  
}
.sticky-filter-bar {
    position: sticky;
    top: 0;
    z-index: 3;
    background-color: #f9f9f9;
    border-bottom: 1px solid #eee;
    min-height: auto !important;
  }
  .native-scroll {
    overflow-x: auto;
    white-space: nowrap;
    -webkit-overflow-scrolling: touch;
    width: 100%;
    /* Hide scrollbar for cleaner look on mobile while keeping touch scroll */
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE/Edge */
    
    &::-webkit-scrollbar {
      display: none; /* Chrome/Safari/Opera */
    }
  }
</style>